"use client"

import { Content } from 'mdast';
import { Cpu, Hourglass, Eye, Waypoints, RotateCcw, type LucideIcon, Globe, createLucideIcon } from 'lucide-react';
import { ChunkDocContext } from '@/lib/parseMarkdown';
import { FootnoteHandler } from '../Markdown';

// ─── Custom "banned algorithm" icon ─────────────────────────────────────────
//
// A prohibition sign over the training algorithm: lucide `Ban`'s circle and
// slash drawn across a nabla (∇, the gradient in the weight-update rule), so
// the glyph reads "no gradient updates" — inference only. The nabla is broken
// where the slash crosses it (the gap lucide's `*-off` icons use to keep both
// shapes legible at small sizes). Built with `createLucideIcon` so it matches
// the other rows' icons exactly (24×24 viewBox, currentColor stroke, round
// caps, and the same `strokeWidth={2}` the render site passes in) and
// satisfies the `LucideIcon` typing of the row arrays below.
const BannedAlgorithm: LucideIcon = createLucideIcon('BannedAlgorithm', [
  ['circle', { cx: '12', cy: '12', r: '10', key: 'ban-circle' }],
  // nabla ∇, with a gap at its upper-left where the slash passes through
  ['path', { d: 'M10.6 8h4.9L12 15l-1.5-3', key: 'nabla' }],
  // prohibition slash across the whole glyph (lucide ban slash)
  ['path', { d: 'm4.9 4.9 14.2 14.2', key: 'ban-slash' }],
]);

// ─── Row content ──────────────────────────────────────────────────────────────
//
// Each box is a title header followed by icon rows. The rows are authored here,
// each pairing a lucide icon with its label. Per the separated-content rule the
// icon component is one cmd-click from the render site (`row.Icon`).
//
// The 2030 box mirrors the four Core Principles from PlanPrinciplesBox — same
// lucide icons (Hourglass / Eye / Waypoints / RotateCcw) so the two blocks read
// as the same list.

const NEGOTIATION_ROWS: { Icon: LucideIcon; label: string }[] = [
  { Icon: Cpu, label: 'Compute Declaration' },
  { Icon: BannedAlgorithm, label: 'Training Pause' },
  { Icon: Globe, label: 'Get Worldwide Buy-in' },
];

const CORE_PRINCIPLE_ROWS: { Icon: LucideIcon; label: string }[] = [
  { Icon: Hourglass, label: 'Buy Time' },
  { Icon: Eye, label: 'Total Research Transparency' },
  { Icon: Waypoints, label: 'Diffuse AI Broadly' },
  { Icon: RotateCcw, label: 'Reversibility' },
];

interface TwoBoxes2029and2030Props {
  // children / docContext / FootnoteHandler are accepted to mirror the sibling
  // directive blocks; unused here because both boxes render fixed rows above.
  children?: Content[];
  docContext?: ChunkDocContext;
  FootnoteHandler?: FootnoteHandler;
}

export const TwoBoxes2029and2030 = ({}: TwoBoxes2029and2030Props) => {
  return (
    <div className="two-boxes-2029-2030 flex flex-col gap-3 lg:flex-row lg:gap-4 lg:items-stretch">
      <div className="flex flex-col rounded-md border-2 border-black lg:w-1/2 justify-between">
        <div className="p-3 lg:p-4 text-2xl leading-none lg:mb-3 lg:pb-2 lg:text-3xl border-b border-black/20 pb-2">
          2029: Hurried Negotiation
        </div>
        <div className="text-base hidden lg:flex text-black/75 px-3 lg:px-4 pt-2 pb-4">
          The US and China negotiate a series of agreements that eventually result in Plan A.
        </div>
        <div className="flex flex-1 flex-col px-3 lg:px-4 mt-auto pt-3 lg:pt-0 pb-2">
          {NEGOTIATION_ROWS.map(({ Icon, label }) => (
            <div key={label} className="flex items-center gap-2.5 lg:gap-3.5 py-[4px] lg:py-[9px]">
              <span
                className="h-5 w-5 shrink-0 text-[#2A623D] [&_svg]:block [&_svg]:h-full [&_svg]:w-full lg:h-[26px] lg:w-[26px]"
                aria-hidden="true"
              >
                <Icon strokeWidth={2} />
              </span>
              <span className="text-xl leading-tight xl:text-[22px]">{label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col rounded-md border-2 border-black lg:w-1/2 justify-between">
        <div className="p-3 lg:p-4 text-2xl leading-none lg:mb-3 lg:pb-2 lg:text-3xl border-b border-black/20 pb-2">
          2030: Plan A is Established
        </div>
        <div className="text-base hidden lg:flex text-black/75 px-3 lg:px-4 flex-col pt-2 pb-2">
          The Consortium negotiates governing principles.
        </div>
        <div className="flex flex-1 flex-col px-3 lg:px-4 pt-3 lg:pt-0 pb-2 mt-auto">
          {CORE_PRINCIPLE_ROWS.map(({ Icon, label }) => (
            <div key={label} className="flex items-center gap-2.5 lg:gap-3.5 py-[4px] lg:py-[9px]">
              <span
                className="h-5 w-5 shrink-0 text-[#2A623D] [&_svg]:block [&_svg]:h-full [&_svg]:w-full lg:h-[26px] lg:w-[26px]"
                aria-hidden="true"
              >
                <Icon strokeWidth={2} />
              </span>
              <span className="text-xl leading-tight xl:text-[22px]">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
